import { StyledCard } from '@common/Configuration/FormValueConfiguration';
import { Entity } from '@common/types/Entity';
import { PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Stack } from '@prophecy/ui-v3';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { produceWithoutFreeze } from '@prophecy/utils/data';
import { uniqueID } from '@prophecy/utils/uniqueID';
import React from 'react';
import styled from 'styled-components';

import { ABIConfigParamType, FileFormats, PipelineEntityType } from '../../../config';
import { useImportState } from '../../useImportState';
import { StyledInput, StyledCenteredCard, StyledChevronRightIcon } from '../styled';
import { ActionTypes } from '../types';
import { DeleteEntityDropdown, EntityRootHeading, useToggleMap } from '../utils';
import { EntityHeading } from './EntityHeading';
import { OptionsHeader, TranspilerOptions } from './TranspilerOptions';

const StyledStack = styled(Stack)`
  flex: 1;
`;

export const PipelineConfiguration = ({ pipeline }: { pipeline: PipelineEntityType }) => {
  const {
    formDataWithActions: {
      formContext: { getValues }
    },
    actions: { updateTranspiledPipeline }
  } = useImportState();

  const { source } = getValues();
  const isAbinitio = source === FileFormats.abinitio;

  const { toggleMap, handleToggleEntityState } = useToggleMap();

  const configurations =
    pipeline?.configurations || (FROZEN_EMPTY_ARRAY as NonNullable<PipelineEntityType['configurations']>);

  const handleConfigChange = (
    value: string | boolean | ABIConfigParamType,
    key: string,
    valueIndex: number,
    action?: ActionTypes,
    isAll?: boolean
  ) => {
    const currentConfig = { ...configurations[valueIndex], [key]: value };
    const updatedConfigs =
      action === ActionTypes.deleteAll
        ? []
        : produceWithoutFreeze(configurations, (draft) => {
            if (action === ActionTypes.all) {
              draft = draft.map((config) => {
                config.enabled = !!isAll;
                return config;
              });
            } else if (action === ActionTypes.delete) {
              draft.splice(valueIndex, 1);
            } else if (action === ActionTypes.add) {
              draft.push({ name: value as string, enabled: true, configParams: { psets: [] } });
            } else {
              draft[valueIndex] = currentConfig;
            }
          });
    updateTranspiledPipeline(pipeline.__pathIndex as number, {
      ...pipeline,
      configurations: updatedConfigs
    });
  };

  const handleConfigOptionsChange = (
    value: string,
    valueIndex: number,
    configIndex: number,
    key: keyof ABIConfigParamType,
    action?: ActionTypes
  ) => {
    const currentConfig = configurations[configIndex]?.configParams || { psets: [] };
    const updatedValues = produceWithoutFreeze(currentConfig, (draft) => {
      if (action === ActionTypes.all) {
        draft[key].splice(valueIndex, 1);
      } else if (action === ActionTypes.delete) {
        draft[key].splice(valueIndex, 1);
      } else if (action === ActionTypes.add) {
        draft[key].push(value);
      } else {
        draft[key][valueIndex] = value;
      }
    });
    handleConfigChange(updatedValues, 'configParams', configIndex);
  };

  const isDisabled = configurations.some((config) => !config.enabled);
  const actions = (
    <Button
      size='s'
      onClick={() =>
        handleConfigChange(
          uniqueID(
            'config_',
            configurations.map((config) => config.name)
          ),
          '',
          0,
          ActionTypes.add
        )
      }
      iconPlacement='left'
      variant='secondaryGray'
      icon={<PlusIcon type='default' />}
      data-test-id='pipeline-configuration-add-configuration-button'>
      Add Configuration
    </Button>
  );
  return (
    <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x16} 0`}>
      <EntityRootHeading
        actions={actions}
        disabled={isDisabled}
        onDelete={() => handleConfigChange('', '', 0, ActionTypes.deleteAll)}
        count={configurations.length}
        entity={Entity.Configuration}
        onChange={(checked) => handleConfigChange('', '', 0, ActionTypes.all, checked)}
      />
      {configurations.map((configuration, configIndex) => {
        const toggleKey = 'config' + configIndex;
        const configOptions = configuration.configParams;
        return (
          <StyledCard key={configIndex}>
            <Stack gap={theme.spaces.x16}>
              <EntityHeading
                entity={'TranspilerConfig'}
                enabled={configuration.enabled}
                onChange={(value, key) => handleConfigChange(value, key, configIndex)}
                title={configuration.name}
                hasToggle={!toggleMap[toggleKey]}
                actions={
                  <DeleteEntityDropdown
                    name={configuration.name}
                    title='Delete Configuration'
                    onClick={() => handleConfigChange('', '', configIndex, ActionTypes.delete)}
                  />
                }
                toggle={() => handleToggleEntityState(toggleKey)}
              />
              {toggleMap[toggleKey] ? null : (
                <>
                  {isAbinitio && (
                    <OptionsHeader
                      heading={'Configuration Psets (*.psets file)'}
                      onChange={(value, valueIndex, action) =>
                        handleConfigOptionsChange(value, valueIndex, configIndex, 'psets', action)
                      }
                    />
                  )}
                  <Stack
                    width='100%'
                    direction='horizontal'
                    align='space-between'
                    alignY='initial'
                    gap={theme.spaces.x12}>
                    {isAbinitio && (
                      <Stack
                        width='53%'
                        gap={theme.spaces.x12}
                        direction='horizontal'
                        align='space-between'
                        alignY='center'>
                        <TranspilerOptions
                          hasHeader={false}
                          heading={''}
                          parentPsetPath={pipeline.filePath}
                          values={configOptions?.psets || []}
                          onChange={(value, valueIndex, action) =>
                            handleConfigOptionsChange(value, valueIndex, configIndex, 'psets', action)
                          }
                        />
                        <StyledChevronRightIcon type='default' color={theme.colors.gray600} />
                      </Stack>
                    )}
                    <StyledStack grow='1'>
                      <StyledCenteredCard background={theme.colors.gray50}>
                        <StyledInput
                          value={configuration.name}
                          label={'Configuration Name'}
                          onChange={(value) => handleConfigChange(value, 'name', configIndex)}
                          data-test-id='pipeline-configuration-name-input'
                        />
                      </StyledCenteredCard>
                    </StyledStack>
                  </Stack>
                </>
              )}
            </Stack>
          </StyledCard>
        );
      })}
    </Stack>
  );
};
